import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listIdCardPrintRequests,
  listIdCardIssueRequests,
  getIdCardPdf,
  getIdCardPreviewPdf,
  markIdCardIssued,
  listMemberTypes,
  listIdCardPrintLogs,
} from "../../services/idCardService";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
  statusColor,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";

// Bulk "Generate All ID Cards" is disabled until its backend support is
// finished — flip this back to true once that's ready. Per-row Generate
// stays on printOne(), which this flag doesn't touch.
const BULK_GENERATE_ENABLED = false;

// pdf.js is only needed by the preview modal, so it's loaded on demand
// (its own chunk) rather than adding ~114 kB to the main bundle.
//
// The worker is served as a static file from public/pdfjs/ (an unmodified
// copy of node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js), NOT
// bundled via `new URL(..., import.meta.url)`: CRA runs every bundled .js
// through Babel, which injects `require("@babel/runtime/...")` calls into the
// worker and breaks it in production ("require is not defined"). The copy
// must match the installed pdfjs-dist version (3.11.174) — re-copy it if
// pdfjs-dist is ever upgraded.
const PDFJS_WORKER_SRC = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.min.js`;
let pdfjsPromise;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/legacy/build/pdf").then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      return pdfjsLib;
    });
  }
  return pdfjsPromise;
}

// ── Modal shell (matches the pattern used by SystemUsers.jsx / Roles.jsx) ───
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "xl" ? "max-w-2xl" : size === "lg" ? "max-w-lg" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm print:hidden"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// Pull a human message out of an axios error.
function errMessage(err, fallback) {
  const data = err?.response?.data;
  if (data?.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first[0]) return first[0];
  }
  return data?.message || fallback;
}

// ── Member field helpers ─────────────────────────────────────────────────
// The backend's Member shape doesn't have a single "full name" field —
// compose it from title + the initials-denoted given name + surname.
const fullName = (m) => [m?.title, m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";

// "Name with Initials" (e.g. "Mr N.K. Perera") — the Print/Issue Queue
// tables show this instead of the spelled-out given name. `initials` holds
// the actual dotted initials (e.g. "N.K."); fall back to
// name_denoted_by_initials for the rare record that only has the spelled-out
// form, same fallback IdCardReprints.jsx uses.
const nameWithInitials = (m) =>
  [m?.title, m?.initials || m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";

// The member's registration/ID code is returned as `member_code` (see
// MemberDashboard.jsx, which shows the same field as "Member ID"). Fall back
// to the alternate key names defensively, then the member id itself, so the
// column degrades gracefully rather than showing "undefined".
const registrationNumber = (m) =>
  m?.member_code || m?.registration_number || m?.reg_no || m?.member_no || m?.registrationNumber || null;

// "Sport/Category" maps to the member's type category (Coach / Club Student /
// Independent) — the closest existing taxonomy on a member record.
const categoryOf = (m) => m?.member_type || m?.category || m?.sport || "—";

const isPaid = (m) => String(m?.payment_status || "").toLowerCase().includes("paid");

// ── Member detail modal ─────────────────────────────────────────────────
// Navigate an already-open browser tab to the generated PDF blob, so it opens
// as a normal new tab (not a popup/print dialog) — used by Generate below.
// `tab` must have been opened synchronously in direct response to the
// triggering click — opening it any later (e.g. after the `await` that
// fetches the PDF) would get blocked as a popup by most browsers. Falls back
// to a fresh window.open() if the tab wasn't available (blocked, or the
// operator closed it while it loaded), which is a best-effort attempt since
// that call is no longer inside the original user gesture.
function openPdfInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) {
    tab.location.href = url;
  } else {
    window.open(url, "_blank");
  }
  // Give the tab time to load the blob before revoking its object URL.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── ID card preview modal ────────────────────────────────────────────────
// Preview (unlike Generate) stays on this page — it's read-only on the
// backend (no card_status check or mutation, see getIdCardPreviewPdf), so
// it's rendered as an in-app modal instead of navigating to a new tab/window.
// Pages are drawn onto <canvas> elements with pdf.js rather than embedded via
// <iframe>, so the browser's built-in PDF viewer toolbar (page nav, zoom,
// search, print/download, annotation tools) never appears — `#toolbar=0`
// would only hide it in Chromium, not Firefox.
function PreviewModal({ member, onClose }) {
  const containerRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let doc;
    Promise.all([getIdCardPreviewPdf(member.id).then((blob) => blob.arrayBuffer()), loadPdfjs()])
      .then(([data, pdfjsLib]) => pdfjsLib.getDocument({ data }).promise)
      .then((loaded) => {
        doc = loaded;
        if (cancelled) loaded.destroy();
        else setPdf(loaded);
      })
      .catch((err) => !cancelled && setError(errMessage(err, "Could not load the ID card preview.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      if (doc) doc.destroy();
    };
  }, [member.id]);

  // Render every page at the container's width, scaled by devicePixelRatio
  // so the card text/QR stay sharp on high-DPI screens.
  useEffect(() => {
    const container = containerRef.current;
    if (!pdf || !container) return;
    let cancelled = false;
    const tasks = [];
    container.innerHTML = "";

    (async () => {
      const cssWidth = container.clientWidth;
      const dpr = window.devicePixelRatio || 1;
      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n);
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = "100%";
        canvas.className = "block bg-white shadow-sm";
        container.appendChild(canvas);
        const task = page.render({ canvasContext: canvas.getContext("2d"), viewport });
        tasks.push(task);
        try {
          await task.promise;
        } catch {
          return; // cancelled mid-render
        }
      }
    })().catch(() => !cancelled && setError("Could not render the ID card preview."));

    return () => {
      cancelled = true;
      tasks.forEach((t) => t.cancel());
    };
    // `loading` too: the container div only mounts once loading flips false,
    // which can land in a later render than `pdf` being set.
  }, [pdf, loading]);

  return (
    <Modal title={`${fullName(member)} — ID Card Preview`} onClose={onClose} size="xl">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading preview…</p>
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-gray-100 bg-gray-100 p-3">
          <div ref={containerRef} className="flex flex-col gap-3" aria-label="ID card preview" />
        </div>
      )}
    </Modal>
  );
}

// ── Mark Issued modal ────────────────────────────────────────────────────
// Collects an optional handover note (the backend's `note` field on
// mark-issued is nullable, ≤500 chars, e.g. "Collected by member") before
// completing the Printed → Issued transition — see markIdCardIssued.
function MarkIssuedModal({ member, submitting, onClose, onSubmit }) {
  const [note, setNote] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(note.trim());
  };

  return (
    <Modal title={`Mark Issued — ${fullName(member)}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Note (optional)</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            maxLength={500}
            placeholder="e.g. Collected by member"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={submitting} className={`${btnPrimary} !w-auto px-6`}>
            {submitting ? "Marking…" : "Confirm Issued"}
          </button>
          <button type="button" onClick={onClose} disabled={submitting} className={`${btnSecondary} !w-auto px-6`}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Confirm Print modal ──────────────────────────────────────────────────
// Generating the PDF is a one-way transition (card_status flips to
// "Printed" server-side, see getIdCardPdf — there's no reprint path), so
// this gate makes the operator explicitly confirm the member's details
// before the "Generate" button's window.open()/printOne() actually fires.
// Centered icon/title/description/buttons layout — same balanced-dialog
// convention as LogViewer.jsx's DeleteModal — rather than the left-aligned
// title-bar Modal shell used by Preview/Mark Issued on this page.
function ConfirmPrintModal({ member, disabled, onClose, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm print:hidden"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-center text-center gap-5">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: "#fee2e2" }}>
            <Icon path={ICONS.printer} className="w-7 h-7" stroke="#ef4444" width={2} />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-bold" style={{ color: TOKENS.NAVY }}>Confirm ID Card Print</h2>
            <p className="text-sm text-gray-500">
              Please confirm that the ID card preview details are correct before printing.
            </p>
          </div>

          <label className="flex items-start gap-2.5 text-left cursor-pointer select-none w-full">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-700 focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">
              I confirm that the ID card preview details for{" "}
              <span className="font-semibold">{fullName(member)}</span>{" "}
              ({registrationNumber(member) || "—"} · {categoryOf(member)}) are correct.
            </span>
          </label>

          <div className="w-full flex items-start gap-2.5 text-left rounded-xl border border-red-200 bg-red-50 p-3">
            <Icon path={ICONS.alertCircle} className="w-5 h-5 flex-shrink-0" stroke="#dc2626" width={2} />
            <p className="text-sm font-medium text-red-600">
              Once printed, it cannot be printed again — please make sure all details are correct.
            </p>
          </div>

          <div className="flex gap-3 w-full">
            <button type="button" onClick={onClose} disabled={disabled} className={`${btnSecondary} flex-1`}>
              Cancel
            </button>
            <button type="button" onClick={onConfirm} disabled={disabled || !confirmed} className={`${btnPrimary} flex-1`}>
              Confirm & Print
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 19 ? s.slice(0, 19) : s;
};

// The logs endpoint returns one consolidated row per member with string
// "True"/"False" Is_printed/Is_issued flags (no per-row `action` field). Same
// look as Pill, but colored from the flag directly — Pill's keyword match
// would paint "Not Issued" green because it contains "issued".
const LogStatusPill = ({ done, yes, no }) => {
  const c = done ? statusColor("issued") : statusColor("");
  return (
    <span
      className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.color }}
    >
      {done ? yes : no}
    </span>
  );
};

// ── Print Logs tab ───────────────────────────────────────────────────────────
// Global, server-paginated audit trail over every print/issue action — who
// printed/issued which member's card, and when (see listIdCardPrintLogs).
// Same filter-bar + server-pagination shape as BookingLogs.jsx, gated by the
// same "view_id_card_print_requests" permission as the rest of this page
// (no dedicated permission exists for this endpoint).
function PrintLogsTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, printed_count: 0, issued_count: 0 });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: DEFAULT_PAGE_SIZE });

  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const searchTimer = useRef(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const loadLogs = useCallback(() => {
    setLoading(true);
    listIdCardPrintLogs({
      page,
      per_page: DEFAULT_PAGE_SIZE,
      action: action || undefined,
      search: search || undefined,
      date_from: fromDate || undefined,
      date_to: toDate || undefined,
    })
      .then((res) => {
        setRows(Array.isArray(res?.logs) ? res.logs : []);
        setSummary(res?.summary || { total: 0, printed_count: 0, issued_count: 0 });
        setMeta({ total: res?.total || 0, page: res?.page || 1, per_page: res?.per_page || DEFAULT_PAGE_SIZE });
      })
      .catch((err) => toast.error(errMessage(err, "Could not load ID card print logs.")))
      .finally(() => setLoading(false));
  }, [page, action, search, fromDate, toDate]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearchInput = (val) => {
    setDraftSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const lastPage = meta.per_page > 0 ? Math.max(1, Math.ceil(meta.total / meta.per_page)) : 1;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-xs text-gray-400">Total Actions</p>
          <p className="text-2xl font-extrabold" style={{ color: TOKENS.NAVY }}>{summary.total}</p>
        </div>
        <div className="bg-blue-50/60 border-2 border-blue-100 rounded-2xl p-4">
          <p className="text-xs font-bold text-blue-700">Printed</p>
          <p className="text-2xl font-extrabold" style={{ color: TOKENS.NAVY }}>{summary.printed_count}</p>
        </div>
        <div className="bg-emerald-50/60 border-2 border-emerald-100 rounded-2xl p-4">
          <p className="text-xs font-bold text-emerald-700">Issued</p>
          <p className="text-2xl font-extrabold" style={{ color: TOKENS.NAVY }}>{summary.issued_count}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className={labelClass}>Search</label>
            <div className="relative">
              <input
                className={`${inputClass} pr-9`}
                placeholder="Member name, email, NIC, staff name…"
                value={draftSearch}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
              <Icon
                path={ICONS.search}
                className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                stroke="#9ca3af"
                width={2}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Action</label>
            <select
              className={inputClass}
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="printed">Printed</option>
              <option value="issued">Issued</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>From</label>
              <input
                type="date"
                className={inputClass}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input
                type="date"
                className={inputClass}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={ICONS.clock} text="No print/issue log entries match these filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[900px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-3 px-4 font-medium">When</th>
                  <th className="py-3 px-4 font-medium">Action</th>
                  <th className="py-3 px-4 font-medium">Member</th>
                  <th className="py-3 px-4 font-medium">Reg. No.</th>
                  <th className="py-3 px-4 font-medium">Category</th>
                  <th className="py-3 px-4 font-medium">Performed By</th>
                  <th className="py-3 px-4 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.member_id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 px-4 text-xs text-gray-500 font-mono whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <LogStatusPill done={row.Is_printed === "True"} yes="Printed" no="Not Printed" />
                        <span className="text-gray-300">·</span>
                        <LogStatusPill done={row.Is_issued === "True"} yes="Issued" no="Not Issued" />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      <p className="font-medium" style={{ color: TOKENS.NAVY }}>{row.member_name || "—"}</p>
                      <p className="text-gray-400">{row.email}</p>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600 font-mono whitespace-nowrap">{row.member_code || "—"}</td>
                    <td className="py-3 px-4 text-xs text-gray-600">{row.member_type || "—"}</td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      <p className="font-medium" style={{ color: TOKENS.NAVY }}>{row.printed_by_name || "—"}</p>
                      <p className="text-gray-400">{row.printed_by_email || ""}</p>
                    </td>
                    {/* One consolidated row per member — the print and issue
                        events each carry their own note (printed_note /
                        issued_note); there is no flat `note` field. */}
                    <td className="py-3 px-4 text-xs text-gray-600 max-w-xs">
                      {row.printed_note || row.issued_note ? (
                        <>
                          {row.printed_note && (
                            <p className="truncate" title={row.printed_note}>
                              <span className="text-gray-400">Printed: </span>{row.printed_note}
                            </p>
                          )}
                          {row.issued_note && (
                            <p className="truncate" title={row.issued_note}>
                              <span className="text-gray-400">Issued: </span>{row.issued_note}
                            </p>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Page {meta.page} of {lastPage} · {meta.total.toLocaleString()} entries
            </p>
            <Pagination page={meta.page} totalPages={lastPage} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function IdCardPrinting() {
  const { can } = useAuth();
  const canPrint = can("print_id_cards");
  const canMarkIssued = can("mark_id_card_issued");

  const [activeTab, setActiveTab] = useState("queues");

  const [records, setRecords] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [previewMember, setPreviewMember] = useState(null); // member currently shown in PreviewModal
  const [printQueue, setPrintQueue] = useState(null); // array of members currently printing
  const [confirmPrintMember, setConfirmPrintMember] = useState(null); // member awaiting "Confirm" in ConfirmPrintModal

  const [issueRecords, setIssueRecords] = useState([]);
  const [issueLoading, setIssueLoading] = useState(true);
  const [issuingIds, setIssuingIds] = useState(() => new Set()); // member ids with a mark-issued call in flight
  const [markingAll, setMarkingAll] = useState(false);
  const [issuingMember, setIssuingMember] = useState(null); // member currently in the Mark Issued modal

  const fetchRecords = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (typeFilter) params.member_type_id = typeFilter;
    listIdCardPrintRequests(params)
      .then(setRecords)
      .catch((err) => toast.error(errMessage(err, "Could not load ID card print requests.")))
      .finally(() => setLoading(false));
  }, [search, typeFilter]);

  useEffect(() => {
    const t = setTimeout(fetchRecords, 300);
    return () => clearTimeout(t);
  }, [fetchRecords]);

  // Same { search, member_type_id } filters as the print queue — the backend
  // handles both queues through the same query builder, just scoped to a
  // different card_status (see idCardService.js).
  const fetchIssueRecords = useCallback(() => {
    setIssueLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (typeFilter) params.member_type_id = typeFilter;
    listIdCardIssueRequests(params)
      .then(setIssueRecords)
      .catch((err) => toast.error(errMessage(err, "Could not load the issue queue.")))
      .finally(() => setIssueLoading(false));
  }, [search, typeFilter]);

  useEffect(() => {
    const t = setTimeout(fetchIssueRecords, 300);
    return () => clearTimeout(t);
  }, [fetchIssueRecords]);

  useEffect(() => {
    listMemberTypes().then(setMemberTypes).catch(() => {});
  }, []);

  // Only members whose payment has actually completed are ever eligible for
  // printing — the backend's "pending print" queue should already reflect
  // this, but we enforce it client-side too as a safety net.
  const eligible = useMemo(() => records.filter(isPaid), [records]);
  const printPage = usePagination(eligible, { resetKey: `${search}|${typeFilter}` });
  const issuePage = usePagination(issueRecords, { resetKey: `${search}|${typeFilter}` });

  // Fetch the backend-rendered PDF for each queued member (one request per
  // card — the API only exposes a single-member endpoint) and open each in
  // its pre-opened tab as the PDF arrives. Generating the PDF is what flips
  // the member's card_status from "Pending Print" to "Printed" server-side
  // (see IdCardController::member) — it no longer also marks the card
  // issued; that now happens explicitly from the Issue Queue section once
  // the card is actually handed over.
  useEffect(() => {
    if (!printQueue || printQueue.length === 0) return undefined;
    let cancelled = false;

    (async () => {
      let generatedCount = 0;
      for (const { member, tab } of printQueue) {
        if (cancelled) return;
        try {
          const blob = await getIdCardPdf(member.id);
          if (cancelled) return;
          openPdfInTab(blob, tab);
          generatedCount += 1;
        } catch (err) {
          tab?.close?.();
          toast.error(errMessage(err, `Could not generate ID card for ${fullName(member)}.`));
        }
      }
      if (cancelled) return;

      if (generatedCount > 0) {
        toast.success(
          generatedCount > 1 ? `${generatedCount} ID cards generated — ready to issue.` : "ID card generated — ready to issue."
        );
        fetchRecords();
        fetchIssueRecords();
      }
      setPrintQueue(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [printQueue, fetchRecords, fetchIssueRecords]);

  const isPrinting = printQueue != null;

  const markOneIssued = async (member, note) => {
    setIssuingIds((prev) => new Set(prev).add(member.id));
    try {
      await markIdCardIssued(member.id, note);
      toast.success(`${fullName(member)} marked as issued.`);
      setIssuingMember(null);
      fetchIssueRecords();
    } catch (err) {
      toast.error(errMessage(err, `Could not mark ${fullName(member)} as issued.`));
    } finally {
      setIssuingIds((prev) => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
    }
  };

  const markAllIssued = async () => {
    if (markingAll || issueRecords.length === 0) return;
    setMarkingAll(true);
    const results = await Promise.allSettled(issueRecords.map((m) => markIdCardIssued(m.id)));
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed === 0) toast.success("All ID cards marked as issued.");
    else toast.error(`${failed} of ${issueRecords.length} could not be marked as issued.`);
    setMarkingAll(false);
    fetchIssueRecords();
  };

  // Tabs must be opened synchronously, inside the click handler itself —
  // opening them later (after the PDF fetch's await) would be blocked as a
  // popup by most browsers since it's no longer inside the user gesture.
  const printOne = (member) => {
    if (isPrinting) return;
    const tab = window.open("", "_blank");
    if (!tab) toast.error("Your browser blocked the new tab. Please allow popups for this site.");
    setPrintQueue([{ member, tab }]);
  };
  const printAll = () => {
    if (isPrinting) return;
    if (eligible.length === 0) {
      toast.error("No eligible members to print.");
      return;
    }
    const queue = eligible.map((member) => ({ member, tab: window.open("", "_blank") }));
    if (queue.some((q) => !q.tab)) {
      toast.error("Your browser blocked one or more new tabs. Please allow popups for this site.");
    }
    setPrintQueue(queue);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-5 print:hidden">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Print ID Cards
        </h1>
        <p className="text-sm text-gray-500">Manage ID card printing and issuance workflow.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 print:hidden">
        {[
          { key: "queues", label: "Print & Issue Queues" },
          { key: "logs", label: "Print Logs" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === t.key ? "text-white" : "text-gray-500 hover:bg-gray-100"
            }`}
            style={activeTab === t.key ? { backgroundColor: TOKENS.PRIMARY } : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "logs" ? (
        <PrintLogsTab />
      ) : (
        <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 print:hidden">
        <div className="bg-blue-50/60 border-2 border-blue-100 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Icon path={ICONS.printer} className="w-7 h-7 text-blue-700" width={1.8} />
            </div>
            <div>
              <p className="text-sm font-bold text-blue-700">Print Queue</p>
              <p className="text-3xl font-extrabold leading-tight" style={{ color: TOKENS.NAVY }}>
                {eligible.length}
              </p>
              <p className="text-xs text-gray-500">Pending ID cards</p>
            </div>
          </div>
          {canPrint && (
            <button
              onClick={printAll}
              disabled={!BULK_GENERATE_ENABLED || eligible.length === 0 || isPrinting}
              title={!BULK_GENERATE_ENABLED ? "Bulk generation is temporarily unavailable — use Generate on each row instead." : undefined}
              className={btnPrimary}
            >
              <Icon path={ICONS.printer} className="w-4 h-4" width={2} />
              {isPrinting ? "Printing…" : "Generate All ID Cards"}
            </button>
          )}
        </div>

        <div className="bg-emerald-50/60 border-2 border-emerald-100 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Icon path={ICONS.check} className="w-7 h-7 text-emerald-700" width={1.8} />
            </div>
            <div>
              <p className="text-sm font-bold text-emerald-700">Issue Queue</p>
              <p className="text-3xl font-extrabold leading-tight" style={{ color: TOKENS.NAVY }}>
                {issueRecords.length}
              </p>
              <p className="text-xs text-gray-500">Ready to issue</p>
            </div>
          </div>
          {canMarkIssued && (
            <button
              onClick={markAllIssued}
              disabled={issueRecords.length === 0 || markingAll}
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:bg-emerald-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-sm px-4 py-2.5 transition shadow-sm hover:shadow-md"
            >
              <Icon path={ICONS.check} className="w-4 h-4" width={2} />
              {markingAll ? "Marking…" : "Mark All Issued"}
            </button>
          )}
        </div>
      </div>

      {/* Filters (shared — same { search, member_type_id } feed both queues) */}
      <div className="flex flex-wrap gap-3 mb-5 print:hidden">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className={`${inputClass} w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All categories</option>
          {memberTypes.map((t) => (
            <option key={t.category_id} value={t.category_id}>
              {t.description}
            </option>
          ))}
        </select>
      </div>

      {/* Print Queue + Issue Queue, side by side on the same page */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
        {/* Print Queue panel */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Icon path={ICONS.printer} className="w-5 h-5 text-blue-700" width={1.8} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-blue-700 tracking-wide">PRINT QUEUE</p>
                <p className="text-xs text-gray-500 truncate">Members with completed payment, pending ID card print.</p>
              </div>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
              {eligible.length} Pending
            </span>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          ) : eligible.length === 0 ? (
            <EmptyState icon={ICONS.card} text="No members with completed payment are pending ID card print." />
          ) : (
            <table className="w-full text-left table-fixed">
              <colgroup>
                <col className="w-[42%]" />
                <col className="w-[23%]" />
                <col className="w-[18%]" />
                <col className="w-[17%]" />
              </colgroup>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Name with Initials</th>
                  <th className="py-2 px-3 font-medium">Reg. No.</th>
                  <th className="py-2 px-3 font-medium">Category</th>
                  <th className="py-2 pl-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {printPage.pageItems.map((m) => (
                  <tr key={m.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={nameWithInitials(m)} size={34} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                            {nameWithInitials(m)}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono truncate">
                      {registrationNumber(m) || "—"}
                    </td>
                    <td className="py-3 px-3">
                      <span className="inline-block max-w-full truncate px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                        {categoryOf(m)}
                      </span>
                    </td>
                    <td className="py-3 pl-3">
                      <div className="flex flex-col items-start gap-1.5">
                        {canPrint && (
                          <>
                            <button
                              onClick={() => setPreviewMember(m)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                              title="Preview ID card (no status change)"
                            >
                              <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                              Preview
                            </button>
                            <button
                              onClick={() => setConfirmPrintMember(m)}
                              disabled={isPrinting}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded-lg px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed transition"
                              title="Generate ID card"
                            >
                              <Icon path={ICONS.printer} className="w-3.5 h-3.5" width={2} />
                              Generate
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {printPage.totalPages > 1 && (
            <div className="pt-4 mt-2 border-t border-gray-100">
              <Pagination page={printPage.page} totalPages={printPage.totalPages} onPageChange={printPage.setPage} />
            </div>
          )}
        </div>

        {/* Issue Queue panel */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Icon path={ICONS.check} className="w-5 h-5 text-emerald-700" width={1.8} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-emerald-700 tracking-wide">ISSUE QUEUE</p>
                <p className="text-xs text-gray-500 truncate">ID cards already printed — mark them issued once handed over.</p>
              </div>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
              {issueRecords.length} Ready
            </span>
          </div>

          {issueLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          ) : issueRecords.length === 0 ? (
            <EmptyState icon={ICONS.card} text="No printed cards are awaiting issue." />
          ) : (
            <table className="w-full text-left table-fixed">
              <colgroup>
                <col className="w-[42%]" />
                <col className="w-[23%]" />
                <col className="w-[18%]" />
                <col className="w-[17%]" />
              </colgroup>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Name with Initials</th>
                  <th className="py-2 px-3 font-medium">Reg. No.</th>
                  <th className="py-2 px-3 font-medium">Category</th>
                  <th className="py-2 pl-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issuePage.pageItems.map((m) => {
                  const issuing = issuingIds.has(m.id);
                  return (
                    <tr key={m.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={nameWithInitials(m)} size={34} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                              {nameWithInitials(m)}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{m.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-600 font-mono truncate">
                        {registrationNumber(m) || "—"}
                      </td>
                      <td className="py-3 px-3">
                        <span className="inline-block max-w-full truncate px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                          {categoryOf(m)}
                        </span>
                      </td>
                      <td className="py-3 pl-3">
                        <div className="flex flex-col items-start gap-1.5">
                          {canMarkIssued && (
                            <button
                              onClick={() => setIssuingMember(m)}
                              disabled={issuing || markingAll}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed transition"
                              title="Mark ID card issued"
                            >
                              <Icon path={ICONS.check} className="w-3.5 h-3.5" width={2} />
                              {issuing ? "Marking…" : "Mark Issued"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {issuePage.totalPages > 1 && (
            <div className="pt-4 mt-2 border-t border-gray-100">
              <Pagination page={issuePage.page} totalPages={issuePage.totalPages} onPageChange={issuePage.setPage} />
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {previewMember && <PreviewModal member={previewMember} onClose={() => setPreviewMember(null)} />}
      {confirmPrintMember && (
        <ConfirmPrintModal
          member={confirmPrintMember}
          disabled={isPrinting}
          onClose={() => setConfirmPrintMember(null)}
          onConfirm={() => {
            printOne(confirmPrintMember);
            setConfirmPrintMember(null);
          }}
        />
      )}
      {issuingMember && (
        <MarkIssuedModal
          member={issuingMember}
          submitting={issuingIds.has(issuingMember.id)}
          onClose={() => setIssuingMember(null)}
          onSubmit={(note) => markOneIssued(issuingMember, note)}
        />
      )}
    </div>
  );
}
